'use client';

import * as React from 'react';
import { AlertCircle, RefreshCw, Home, Loader2 } from 'lucide-react';
import Link from 'next/link';

import { useCaptureError } from '@workspace/monitoring/hooks/use-capture-error';
import { Button } from '@workspace/ui/components/button';

import { isNavigatingExternal } from '~/lib/navigation';

export default function Error({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Check if this error occurred during external navigation
  const [isNavigating, setIsNavigating] = React.useState(false);
  const [shouldCapture, setShouldCapture] = React.useState(false);

  React.useEffect(() => {
    // Check navigation flag
    if (isNavigatingExternal()) {
      setIsNavigating(true);
      setShouldCapture(false);
      return;
    }
    // Mark error for capture in monitoring
    setShouldCapture(true);
  }, [error]);

  // Capture non-navigation errors in monitoring
  useCaptureError(shouldCapture ? error : null);

  // During navigation, show a loading state instead of error
  // This prevents the error flash when navigating to external URLs
  if (isNavigating) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="mt-4 text-sm text-muted-foreground">Redirecting...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-16">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Something went wrong
          </p>
          <h1 className="text-2xl font-semibold">We hit an unexpected error</h1>
          <p className="text-sm text-muted-foreground">
            Try again. If this keeps happening, refresh the page.
            {error?.digest ? ` (Error ID: ${error.digest})` : ''}
          </p>
          {process.env.NODE_ENV === 'development' && error?.message && (
            <p className="text-xs text-destructive mt-2 font-mono bg-destructive/10 p-2 rounded">
              {error.message}
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={reset} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
          <Button variant="outline" asChild>
            <Link href="/organizations" className="gap-2">
              <Home className="h-4 w-4" />
              Go to Dashboard
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
